/**
 * Shared helpers for the model-authored "intent summary" blurbs shown on tool calls.
 *
 * Tools whose raw inputs (SQL, code, search terms, bare IDs) are opaque to users carry a
 * model-authored, plain-language statement of intent. The same summary is surfaced inline in the
 * progress "Thinking" section (see ChatProgress) and at the top of the Tool Details modal (see
 * ToolDetail), so the extraction logic lives here to keep both call sites in sync.
 */

import {ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {ToolType, isToolType} from 'llm/chat-library/tool-details/types';

const MAX_TOOL_INPUT_SUMMARY_LENGTH = 180;

/**
 * The tool argument that carries a model-authored, plain-language statement of intent for
 * tools whose raw inputs (SQL, code, search terms) are opaque to users. Must match the name of
 * the `intent_summary` field declared on the backend tools (QueryWarehouseTool, RunCodeTool,
 * SearchTool) in benchling/llm/tools/.
 */
export const INTENT_SUMMARY_ARG = 'intent_summary';

/**
 * The ResearchSubAgent tool names its intent blurb `prompt_summary` rather than `intent_summary`.
 */
const PROMPT_SUMMARY_ARG = 'prompt_summary';

/**
 * Extract a compact, one-sentence summary from a string tool argument for the progress UI.
 *
 * Enforces the intended format -- a single sentence, length-capped -- so a model-authored
 * summary stays terse regardless of what the model produces. Returns null when the argument is
 * missing or empty so callers can fall back to a static label or the raw query terms.
 */
export function getOneSentenceSummary(args: Record<string, unknown>, key: string): string | null {
  const summary = args[key];
  if (typeof summary !== 'string') {
    return null;
  }
  const trimmedSummary = summary.trim();
  if (!trimmedSummary) {
    return null;
  }

  // Enforce the intended one-sentence summary format for compact progress UI.
  const firstSentenceMatch = trimmedSummary.match(/^.*?[.!?](?:\s|$)/);
  const oneSentenceSummary = (firstSentenceMatch ? firstSentenceMatch[0] : trimmedSummary).trim();
  if (oneSentenceSummary.length <= MAX_TOOL_INPUT_SUMMARY_LENGTH) {
    return oneSentenceSummary;
  }
  return `${oneSentenceSummary.slice(0, MAX_TOOL_INPUT_SUMMARY_LENGTH - 3).trimEnd()}...`;
}

/**
 * The model-authored intent summary for a tool call, or null when the tool/model didn't supply one.
 * Reads the standard `intent_summary` arg, falling back to ResearchSubAgent's `prompt_summary`.
 * Used by the Tool Details modal to show the blurb at the top of the body; matches the inline
 * summary shown in the progress section.
 */
export function getToolIntentSummary(toolCall: ProgressToolCall): string | null {
  const {tool, args} = toolCall;
  if (!args) {
    return null;
  }
  const summaryArg =
    isToolType(tool) && tool === ToolType.ResearchSubAgent ? PROMPT_SUMMARY_ARG : INTENT_SUMMARY_ARG;
  return getOneSentenceSummary(args, summaryArg);
}
